import React, { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Product, PaginatedResponse, getErrorMessage } from '../types';
import api from '../api';
import { User } from '../interfaces/User';
import { ImageCarousel } from './ImageCarousel';
import { PhotoManager, PhotoDraft, photoDraftsFromProduct, revokePendingPreviews } from './PhotoManager';
import { useTranslation } from '../i18n';
import { useApiRequest } from '../useApiRequest';
import { Tooltip } from './Tooltip';
import { Modal } from './Modal';
import { FormLabel } from './FormLabel';
import { FormLegend } from './FormLegend';
import { FieldError } from './FieldError';
import { validateFormSubmit } from '../utils/formValidation';
import {
  suggestedRenterFixedFee,
  suggestedRenterFeePerDay,
  adjustedSuggestedRenterFeePerDay,
  renterSubtotal,
  rondoshareCommission,
  ownerFromRenter,
  effectivePerDay,
  estimateCurrentPrice,
  MAX_ESTIMATED_CURRENT_PRICE,
  PREVIEW_DAYS,
  PRODUCT_CATEGORIES,
  ProductCategory,
  PricingStrategy,
} from '../pricing';
import { categoryLabelKey } from '../categories';
import {
  OfferEligibility,
  shouldShowOfferEligibilityBanner,
} from '../offerEligibility';


interface ManageProductsProps {
  user: User | null;
  setError: (error: string) => void;
  onOfferEligibilityChange?: () => void;
}

interface NewProductDraft {
  editing: Partial<Product>;
  photoDrafts: PhotoDraft[];
  renterFeeTouched: boolean;
  renterFixedFeeTouched: boolean;
  estimatorNewPrice: string;
  estimatorAgeYears: string;
  formError: string;
  fieldErrors: Record<string, string>;
}

/** Unsaved "add product" input, kept outside the component so it survives navigating away from /manage. */
let newProductDraft: NewProductDraft | null = null;

const emptyNewProduct = (): Partial<Product> => ({
  name: '',
  description: '',
  estimatedCurrentPrice: 0,
  renterFixedFee: 0,
  renterFeePerDay: 0,
  pricingStrategy: 'sustainable',
  price: 0,
  images: [],
  isFeatured: false,
});

export const ManageProducts: React.FC<ManageProductsProps> = ({
  user,
  setError,
  onOfferEligibilityChange,
}) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [editing, setEditing] = useState<Partial<Product> | null>(null);
  const [renterFeeTouched, setRenterFeeTouched] = useState(false);
  const [renterFixedFeeTouched, setRenterFixedFeeTouched] = useState(false);
  const [photoDrafts, setPhotoDrafts] = useState<PhotoDraft[]>([]);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [offerEligibility, setOfferEligibility] = useState<OfferEligibility | null>(null);
  const [estimatorNewPrice, setEstimatorNewPrice] = useState('');
  const [estimatorAgeYears, setEstimatorAgeYears] = useState('');
  // Unavailable periods moved to Account -> Availability tab.

  const fetchOfferEligibility = useCallback(async () => {
    try {
      const res = await api.get<OfferEligibility>('/users/offer-eligibility');
      setOfferEligibility(res.data);
    } catch {
      setOfferEligibility(null);
    }
  }, []);

  useEffect(() => {
    fetchOfferEligibility();
  }, [fetchOfferEligibility]);

  const listingsHidden = shouldShowOfferEligibilityBanner(offerEligibility);

  const { loading: saving, call: saveRequest } = useApiRequest(async () => {
    if (!editing) return;

    const existing = photoDrafts.filter((p): p is Extract<PhotoDraft, { kind: 'existing' }> => p.kind === 'existing');
    const pending = photoDrafts.filter((p): p is Extract<PhotoDraft, { kind: 'pending' }> => p.kind === 'pending');
    let images = existing.map(p => ({ id: p.id, imageUrl: p.imageUrl }));
    if (pending.length > 0) {
      const formData = new FormData();
      pending.forEach(p => formData.append('images', p.file));
      try {
        const res = await api.post<{ imageUrls: string[] }>(`/products/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const newImages = res.data.imageUrls.map(url => ({ id: 0, imageUrl: url }));
        images = [...images, ...newImages];
      } catch (err) { setFormError(getErrorMessage(err, t('manage.upload_error'))); return; }
    }

    try {
      const renterDaily = editing.renterFeePerDay ?? 0;
      const originalPrice = estimatorNewPrice !== ''
        ? parseWholeNumber(estimatorNewPrice)
        : editing.originalPrice ?? null;
      const ageYears = estimatorAgeYears !== ''
        ? parseWholeNumber(estimatorAgeYears)
        : editing.ageYears ?? null;
      const data = { ...editing, images, price: renterDaily, originalPrice, ageYears };
      if (editing.id) await api.put(`/products/${editing.id}`, data);
      else await api.post(`/products`, data);
      closeAfterSave();
      fetchProducts();
      await fetchOfferEligibility();
      onOfferEligibilityChange?.();
    } catch (err) { setFormError(getErrorMessage(err, t('manage.save_error'))); }
  });

  const fetchProducts = useCallback(async () => {
    try {
      const res = await api.get<PaginatedResponse<Product>>(`/products?editableOnly=true&pageSize=100`);
      setProducts(res.data.items);
    } catch (err) { setError(getErrorMessage(err, t('manage.load_error'))); }
  }, [setError, t]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openCreateForm = useCallback(() => {
    const draft = newProductDraft;
    setFormError(draft?.formError ?? '');
    setFieldErrors(draft?.fieldErrors ?? {});
    setRenterFeeTouched(draft?.renterFeeTouched ?? false);
    setRenterFixedFeeTouched(draft?.renterFixedFeeTouched ?? false);
    setPhotoDrafts(draft?.photoDrafts ?? []);
    setEstimatorNewPrice(draft?.estimatorNewPrice ?? '');
    setEstimatorAgeYears(draft?.estimatorAgeYears ?? '');
    setEditing(draft?.editing ?? emptyNewProduct());
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!user) return;
    if (searchParams.get('new') === '1') {
      autoOpenedRef.current = true;
      openCreateForm();
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, openCreateForm, user]);

  const saveProduct = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setFormError('');
    setFieldErrors({});
    if (!validateFormSubmit(e)) return;

    const estimated = editing.estimatedCurrentPrice ?? 0;
    const renterFixed = editing.renterFixedFee ?? 0;
    const renterDaily = editing.renterFeePerDay ?? 0;
    const errors: Record<string, string> = {};
    if (editing.category == null) {
      errors.category = t('manage.validation_category');
    }
    if (estimated <= 0) {
      errors.estimatedPrice = t('manage.validation_estimated_price');
    } else if (estimated > MAX_ESTIMATED_CURRENT_PRICE) {
      errors.estimatedPrice = t('manage.validation_estimated_price_max', { max: MAX_ESTIMATED_CURRENT_PRICE });
    }
    if (renterFixed < 0) {
      errors.renterFixedFee = t('manage.validation_renter_fixed_fee');
    }
    if (renterDaily <= 0 && renterFixed <= 0) {
      errors.renterFee = t('manage.validation_renter_fee');
    }
    const existing = photoDrafts.filter((p): p is Extract<PhotoDraft, { kind: 'existing' }> => p.kind === 'existing');
    const pending = photoDrafts.filter((p): p is Extract<PhotoDraft, { kind: 'pending' }> => p.kind === 'pending');
    if (existing.length + pending.length < 1) {
      errors.images = t('manage.validation_images');
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    await saveRequest();
  };

  const deleteProduct = async (id: number) => {
    if (!window.confirm(t('manage.delete_confirm'))) return;
    try {
      await api.delete(`/products/${id}`);
      fetchProducts();
      await fetchOfferEligibility();
      onOfferEligibilityChange?.();
    } catch (err) { setError(getErrorMessage(err, t('manage.delete_error'))); }
  };

  const openEditForm = (product: Product) => {
    setFormError('');
    setFieldErrors({});
    setRenterFeeTouched(true);
    setRenterFixedFeeTouched(true);
    setEditing(product);
    setPhotoDrafts(photoDraftsFromProduct(product.images));
    setEstimatorNewPrice(product.originalPrice != null && product.originalPrice > 0
      ? String(product.originalPrice)
      : '');
    setEstimatorAgeYears(product.ageYears != null ? String(product.ageYears) : '');
  };

  const clearProductForm = () => {
    setPhotoDrafts([]);
    setEditing(null);
    setFormError('');
    setFieldErrors({});
    setRenterFeeTouched(false);
    setRenterFixedFeeTouched(false);
    setEstimatorNewPrice('');
    setEstimatorAgeYears('');
  };

  /** Dismiss without saving: an unfinished new product is kept so reopening restores it. */
  const closeProductModal = () => {
    if (editing && !editing.id) {
      newProductDraft = {
        editing,
        photoDrafts,
        renterFeeTouched,
        renterFixedFeeTouched,
        estimatorNewPrice,
        estimatorAgeYears,
        formError,
        fieldErrors,
      };
    } else {
      revokePendingPreviews(photoDrafts);
    }
    clearProductForm();
  };

  const closeAfterSave = () => {
    if (editing && !editing.id) newProductDraft = null;
    revokePendingPreviews(photoDrafts);
    clearProductForm();
  };

  const parseWholeNumber = (raw: string): number => {
    if (raw === '') return 0;
    return Math.max(0, Math.trunc(Number(raw)));
  };

  const applySuggestedFees = (
    next: Partial<Product>,
    estimatedPrice: number,
    category: ProductCategory,
    resetFixed = false,
    resetDaily = false,
  ) => {
    const strategy = next.pricingStrategy ?? 'sustainable';
    if (estimatedPrice <= 0) return next;
    if (resetFixed || !renterFixedFeeTouched) {
      next.renterFixedFee = suggestedRenterFixedFee(estimatedPrice, category, strategy);
    }
    if (resetDaily || !renterFeeTouched) {
      next.renterFeePerDay = adjustedSuggestedRenterFeePerDay(estimatedPrice, category, strategy);
    }
    return next;
  };

  const updatePricingStrategy = (strategy: PricingStrategy) => {
    if (!editing) return;
    const category = editing.category ?? ProductCategory.Other;
    const estimatedPrice = editing.estimatedCurrentPrice ?? 0;
    const next = applySuggestedFees(
      { ...editing, pricingStrategy: strategy },
      estimatedPrice,
      category,
      false,
      !renterFeeTouched,
    );
    setEditing(next);
  };

  const updateCategory = (category: ProductCategory) => {
    if (!editing) return;
    const estimatedPrice = editing.estimatedCurrentPrice ?? 0;
    const next = applySuggestedFees({ ...editing, category }, estimatedPrice, category);
    setEditing(next);
    setFieldErrors(prev => ({ ...prev, category: '' }));
  };

  const updateEstimatedPrice = (value: number) => {
    if (!editing) return;
    const category = editing.category ?? ProductCategory.Other;
    const capped = Math.min(value, MAX_ESTIMATED_CURRENT_PRICE);
    const next = applySuggestedFees(
      { ...editing, estimatedCurrentPrice: capped },
      capped,
      category,
    );
    setEditing(next);
  };

  const runValueEstimator = () => {
    if (!editing || editing.category == null) {
      setFieldErrors(prev => ({ ...prev, category: t('manage.validation_category') }));
      return;
    }
    const newPrice = parseWholeNumber(estimatorNewPrice);
    const ageYears = parseWholeNumber(estimatorAgeYears);
    if (newPrice <= 0) {
      setFieldErrors(prev => ({ ...prev, estimatorNewPrice: t('manage.validation_estimator_new_price') }));
      return;
    }
    const estimated = Math.min(
      estimateCurrentPrice(newPrice, ageYears, editing.category),
      MAX_ESTIMATED_CURRENT_PRICE,
    );
    setRenterFeeTouched(false);
    setRenterFixedFeeTouched(false);
    const next = applySuggestedFees(
      { ...editing, estimatedCurrentPrice: estimated },
      estimated,
      editing.category,
      true,
      true,
    );
    setEditing(next);
    setFieldErrors(prev => ({ ...prev, estimatorNewPrice: '', estimatedPrice: '' }));
  };

  const estimated = editing?.estimatedCurrentPrice ?? 0;
  const category = editing?.category ?? ProductCategory.Other;
  const renterFixed = editing?.renterFixedFee ?? 0;
  const renterDaily = editing?.renterFeePerDay ?? 0;
  const previewSubtotal = renterSubtotal(renterFixed, renterDaily, PREVIEW_DAYS, 1);
  const previewCommission = rondoshareCommission(previewSubtotal);
  const previewOwner = ownerFromRenter(previewSubtotal);

  const formatProductPrice = (product: Product) =>
    t('products.price_fixed_daily', {
      fixed: product.renterFixedFee ?? 0,
      daily: product.renterFeePerDay ?? product.price,
    });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', marginRight: '1rem'  }}>
        <h2 className="section-title" style={{ margin: 0 }}>{t('manage.title')}</h2>
        <button
          className="btn btn-primary"
          onClick={openCreateForm}
        >
          {t('manage.add_product')}
        </button>
      </div>

      {/* Unavailable periods moved to Account -> Availability tab. */}

      <Modal
        open={editing != null}
        onClose={closeProductModal}
        title={editing ? (editing.id ? t('manage.modal_edit') : t('manage.modal_new')) : undefined}
        panelClassName="modal--wide modal--scroll"
      >
        {editing && (
          <form onSubmit={saveProduct}>
            {formError && (
              <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>
                {formError}
                <button type="button" className="dismiss" onClick={() => setFormError('')}>&times;</button>
              </div>
            )}
            <FormLegend />
            <div className="form-group">
              <FormLabel required>{t('manage.name')}</FormLabel>
              <input type="text" required value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })} disabled={saving} aria-required="true" />
            </div>
            <div className="form-group">
              <FormLabel required>{t('manage.description')}</FormLabel>
              <textarea required value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} disabled={saving} aria-required="true" />
            </div>
            <div className={`form-group${fieldErrors.category ? ' form-group--error' : ''}`}>
              <FormLabel required>{t('manage.category_label')}</FormLabel>
              <select
                required
                value={editing.category ?? ''}
                onChange={e => updateCategory(Number(e.target.value) as ProductCategory)}
                disabled={saving}
                aria-required="true"
                aria-invalid={fieldErrors.category ? true : undefined}
                aria-describedby={fieldErrors.category ? 'manage-category-error' : undefined}
              >
                <option value="" disabled>{t('manage.category_placeholder')}</option>
                {PRODUCT_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{t(categoryLabelKey(cat))}</option>
                ))}
              </select>
              <FieldError id="manage-category-error" message={fieldErrors.category} />
            </div>

            {editing.category != null && (
              <div className="pricing-breakdown" style={{ marginBottom: '1rem', padding: '1rem', background: 'var(--color-surface-alt, #f8f9fa)', borderRadius: '8px' }}>
                <strong>{t('manage.estimator_title')}</strong>
                <p className="text-muted" style={{ fontSize: '0.85rem', margin: '0.5rem 0' }}>
                  {t('manage.estimator_help')}{' '}
                </p>
                <div className="form-row">
                  <div className={`form-group${fieldErrors.estimatorNewPrice ? ' form-group--error' : ''}`}>
                    <FormLabel>
                      {t('manage.estimator_new_price')}
                      <Tooltip content={t('manage.new_price_help')} />
                    </FormLabel>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={estimatorNewPrice}
                      onChange={e => {
                        setEstimatorNewPrice(e.target.value);
                        setFieldErrors(prev => ({ ...prev, estimatorNewPrice: '' }));
                      }}
                      disabled={saving}
                      aria-invalid={fieldErrors.estimatorNewPrice ? true : undefined}
                      aria-describedby={fieldErrors.estimatorNewPrice ? 'manage-estimator-new-price-error' : undefined}
                    />
                    <FieldError id="manage-estimator-new-price-error" message={fieldErrors.estimatorNewPrice} />
                  </div>
                  <div className="form-group">
                    <FormLabel>{t('manage.estimator_age')}</FormLabel>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={estimatorAgeYears}
                      onChange={e => setEstimatorAgeYears(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={runValueEstimator}
                  disabled={saving}
                >
                  {t('manage.estimator_button')}
                </button>
              </div>
            )}

            <div className="form-row">
              <div className={`form-group${fieldErrors.estimatedPrice ? ' form-group--error' : ''}`}>
                <FormLabel required>
                  {t('manage.estimated_price_label')}{' '}
                  <Tooltip content={t('manage.estimated_price_help', { max: MAX_ESTIMATED_CURRENT_PRICE })} />
                </FormLabel>
                <input
                  type="number"
                  min={0}
                  max={MAX_ESTIMATED_CURRENT_PRICE}
                  step={1}
                  required
                  value={editing.estimatedCurrentPrice || ''}
                  onChange={e => {
                    updateEstimatedPrice(parseWholeNumber(e.target.value));
                    setFieldErrors(prev => ({ ...prev, estimatedPrice: '' }));
                  }}
                  disabled={saving}
                  aria-required="true"
                  aria-invalid={fieldErrors.estimatedPrice ? true : undefined}
                  aria-describedby={fieldErrors.estimatedPrice ? 'manage-estimated-price-error' : undefined}
                />
                <FieldError id="manage-estimated-price-error" message={fieldErrors.estimatedPrice} />
              </div>
            </div>
            <div className="form-group">
              <FormLabel>{t('manage.pricing_strategy_label')}</FormLabel>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {([
                  { value: 'generous', multiplier: '0.5', labelKey: 'manage.pricing_strategy_generous' },
                  { value: 'sustainable', multiplier: '1', labelKey: 'manage.pricing_strategy_sustainable' },
                  { value: 'commercial', multiplier: '1.5', labelKey: 'manage.pricing_strategy_commercial' },
                ] as const).map(option => (
                  <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="pricing-strategy"
                      value={option.value}
                      checked={(editing.pricingStrategy ?? 'sustainable') === option.value}
                      onChange={e => updatePricingStrategy(e.target.value as PricingStrategy)}
                      disabled={saving}
                    />
                    <span>{t(option.labelKey)}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="pricing-breakdown" style={{ marginBottom: '1rem', padding: '1rem', background: 'var(--color-surface-alt, #f8f9fa)', borderRadius: '8px' }}>
              <div className="form-row">
                <div className={`form-group${fieldErrors.renterFixedFee ? ' form-group--error' : ''}`}>
                  <FormLabel>
                    {t('manage.renter_fixed_fee_label')}{' '}
                    <Tooltip content={t('manage.renter_fixed_fee_help')} />
                  </FormLabel>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={editing.renterFixedFee || ''}
                    onChange={e => {
                      setRenterFixedFeeTouched(true);
                      setEditing({ ...editing, renterFixedFee: parseWholeNumber(e.target.value) });
                      setFieldErrors(prev => ({ ...prev, renterFixedFee: '' }));
                    }}
                    disabled={saving}
                    aria-invalid={fieldErrors.renterFixedFee ? true : undefined}
                    aria-describedby={fieldErrors.renterFixedFee ? 'manage-renter-fixed-fee-error' : undefined}
                  />
                  <FieldError id="manage-renter-fixed-fee-error" message={fieldErrors.renterFixedFee} />
                  {estimated > 0 && !renterFixedFeeTouched && (() => {
                    const suggestedFixedFee = suggestedRenterFixedFee(estimated, category);
                    return (
                      <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                        {t('manage.renter_fixed_fee_suggested').replace('{amount}', String(suggestedFixedFee))}
                      </p>
                    );
                  })()}
                </div>
                <div className={`form-group${fieldErrors.renterFee ? ' form-group--error' : ''}`}>
                  <FormLabel>
                    {t('manage.renter_fee_label')}{' '}
                    <Tooltip content={t('manage.renter_fee_help')} />
                  </FormLabel>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={editing.renterFeePerDay || ''}
                    onChange={e => {
                      setRenterFeeTouched(true);
                      setEditing({ ...editing, renterFeePerDay: parseWholeNumber(e.target.value) });
                      setFieldErrors(prev => ({ ...prev, renterFee: '' }));
                    }}
                    disabled={saving}
                    aria-invalid={fieldErrors.renterFee ? true : undefined}
                    aria-describedby={fieldErrors.renterFee ? 'manage-renter-fee-error' : undefined}
                  />
                  <FieldError id="manage-renter-fee-error" message={fieldErrors.renterFee} />
                  {estimated > 0 && !renterFeeTouched && (() => {
                    const suggestedFeePerDay = suggestedRenterFeePerDay(estimated, category);
                    return (
                      <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                        {t('manage.renter_fee_suggested').replace('{amount}', String(suggestedFeePerDay))}
                      </p>
                    );
                  })()}
                </div>
              </div>

              <div style={{ marginTop: '0.75rem' }}>
                <div className="guarantee-banner">
                  <img src="/guarantee.png" alt="" />
                  <div>
                    <div>{t('manage.guarantee_banner_text')}</div>
                    <div style={{ fontWeight: 500, marginTop: 4 }}>
                      <Link to="/guarantee">{t('manage.fee_guarantee_link')}</Link>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pricing-total" style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--color-border, #dee2e6)' }}>
                <strong>{t('manage.commission_preview_label', { days: PREVIEW_DAYS })}</strong>
                {renterDaily > 0 || renterFixed > 0 ? (
                  <div className="price-breakdown" style={{ marginTop: '0.5rem' }}>
                    <div className="price-breakdown__row">
                      <span>{t('manage.commission_preview_total')}</span>
                      <span>
                        {t('manage.commission_preview_rental_detail', {
                          fixed: renterFixed,
                          daily: renterDaily,
                          days: PREVIEW_DAYS,
                          subtotal: previewSubtotal,
                        })}
                      </span>
                    </div>
                    <div className="price-breakdown__row">
                      <span>{t('manage.commission_preview_platform')}</span>
                      <span>{previewCommission} Kč</span>
                    </div>
                    <div className="price-breakdown__row price-breakdown__row--total">
                      <span>{t('manage.commission_preview_owner')}</span>
                      <span>
                        {t('manage.commission_preview_owner_detail', {
                          amount: previewOwner,
                          perDay: effectivePerDay(previewOwner, PREVIEW_DAYS),
                        })}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p style={{ margin: '0.25rem 0 0' }}>{t('manage.commission_preview_pending')}</p>
                )}
                <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.75rem', marginBottom: 0 }}>
                  {t('manage.fee_explanation')}{' '}
                  <Link target="_blank" to="/guarantee">{t('manage.fee_guarantee_link')}</Link>
                </p>
              </div>
            </div>

            {user?.isAdmin && (
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={!!editing.isFeatured}
                    onChange={e => setEditing({ ...editing, isFeatured: e.target.checked })}
                    disabled={saving}
                    style={{ marginRight: '0.5rem' }}
                  />
                  {t('manage.featured_label')}{' '}
                  <Tooltip content={t('manage.featured_help')} />
                </label>
              </div>
            )}

            <div className={`form-group${fieldErrors.images ? ' form-group--error' : ''}`}>
              <FormLabel required>
                {t('manage.images')}{' '}
                <Tooltip content={t('manage.images_help')} />
              </FormLabel>
              <PhotoManager
                photos={photoDrafts}
                onChange={photos => {
                  setPhotoDrafts(photos);
                  setFormError('');
                  setFieldErrors(prev => ({ ...prev, images: '' }));
                }}
                disabled={saving}
              />
              <FieldError id="manage-images-error" message={fieldErrors.images} />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving && <span className="btn-spinner" aria-hidden="true" />}
                {t('manage.add')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={closeProductModal}>{t('manage.cancel')}</button>
            </div>
          </form>
        )}
      </Modal>

      <div className="card">
        {products.length === 0 && <p className="text-muted">{t('manage.none')}</p>}
        {products.map(p => {
          const canEdit = Boolean(user && (user.isAdmin || user.id === p.createdById));
          return (
            <div
              key={p.id}
              className={`manage-item${canEdit ? ' manage-item--clickable' : ''}`}
              onClick={canEdit ? () => openEditForm(p) : undefined}
            >
              <div className="item-info">
                <ImageCarousel images={p.images} size={40} />
                <div className="item-details">
                  <div className="item-name">
                    <strong>{p.name}</strong>
                    {listingsHidden && (
                      <span className="badge badge-warning" style={{ marginLeft: 8 }}>
                        {t('offer_eligibility.hidden_badge')}
                      </span>
                    )}
                    {user?.isAdmin && p.isFeatured && (
                      <span className="text-muted" style={{ marginLeft: 8 }}>({t('manage.featured_badge')})</span>
                    )}
                  </div>
                  <span className="text-muted item-price">{formatProductPrice(p)}</span>
                  {user?.isAdmin && p.createdByUsername && (
                    <span className="text-muted">{t('manage.owner', { username: p.createdByUsername })}</span>
                  )}
                </div>
              </div>
              {canEdit && (
                <div className="item-actions">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={e => {
                      e.stopPropagation();
                      openEditForm(p);
                    }}
                  >
                    {t('manage.edit')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={e => {
                      e.stopPropagation();
                      deleteProduct(p.id);
                    }}
                  >
                    {t('manage.delete')}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
